import { authFetch } from './auth';

export interface SendEmailPayload {
  /** Optional — the server falls back to the signed-in admin's saved mailbox. */
  zohoEmail?: string;
  zohoPassword?: string;
  to: string;
  subject: string;
  message: string;
  recipientName?: string;
  senderName?: string;
  senderTitle?: string;
  senderPhone?: string;
  cc?: string;
  bcc?: string;
}

export interface SendEmailResponse {
  success: boolean;
  message: string;
  messageId?: string;
  accepted?: string[];
  error?: string;
}

export interface EmailStatusResponse {
  mode: string;
  message: string;
  host: string;
  port: number;
  secure: boolean;
}

export const getEmailStatus = (): Promise<EmailStatusResponse> =>
  authFetch('/api/email/status');

export const sendEmail = (payload: SendEmailPayload): Promise<SendEmailResponse> =>
  authFetch('/api/email/send', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/* ------------------------------------------------------------------ */
/* Bulk sending                                                        */
/* ------------------------------------------------------------------ */

export interface BulkRecipient {
  email: string;
  name?: string;
  phone?: string;
}

export interface BulkSendPayload {
  /** Optional — the server falls back to the signed-in admin's saved mailbox. */
  zohoEmail?: string;
  zohoPassword?: string;
  recipients: BulkRecipient[];
  subjectTemplate: string;
  messageTemplate: string;
  greeting?: string;
  signOff?: string;
  senderName?: string;
  senderTitle?: string;
  senderPhone?: string;
  bcc?: string;
  delayMs?: number;
}

export interface BulkSendResult {
  email: string;
  name?: string;
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface BulkSendResponse {
  success: boolean;
  sent: number;
  failed: number;
  message: string;
  results: BulkSendResult[];
}

/**
 * Recipients per HTTP request. Deliberately small: the API has no raised
 * maxDuration, so each call must finish well inside the default serverless
 * timeout. If a request ever does time out its whole batch is reported failed
 * even though some may have gone out, so a small batch keeps that blast
 * radius to a handful of addresses.
 */
export const BULK_BATCH_SIZE = 5;

export const sendBulkEmail = (payload: BulkSendPayload): Promise<BulkSendResponse> =>
  authFetch('/api/email/send-bulk', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/* ------------------------------------------------------------------ */
/* Preview                                                             */
/* ------------------------------------------------------------------ */

export interface PreviewPayload {
  subjectTemplate: string;
  messageTemplate: string;
  greeting?: string;
  signOff?: string;
  senderName?: string;
  senderTitle?: string;
  senderPhone?: string;
  recipient?: BulkRecipient;
}

export interface PreviewResponse {
  subject: string;
  html: string;
  text: string;
  recipient: BulkRecipient;
}

/**
 * Renders the campaign through the same template the sender uses, so a
 * downloaded copy matches the real email instead of a lookalike built here.
 */
export const previewEmail = (payload: PreviewPayload): Promise<PreviewResponse> =>
  authFetch('/api/email/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
